/**
 * DatePickerFR — Sélecteur de date FR strict (JJ/MM/AAAA, semaine lun-dim).
 *
 * Drop-in replacement pour <Input type="date"> — même API string ISO
 * "YYYY-MM-DD" en value/onChange, mais affichage FR indépendant de la
 * locale OS du navigateur (correctif iter11).
 *
 * Usage:
 *   <DatePickerFR value={date} onChange={setDate} />
 *   <DatePickerFR value={date} onChange={setDate} disabled className="font-mono" />
 */
import * as React from "react";
import { format, parse, isValid } from "date-fns";
import { fr } from "date-fns/locale";
import { Calendar as CalendarIcon } from "lucide-react";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

function isoToDate(iso) {
  if (!iso) return undefined;
  const d = parse(iso, "yyyy-MM-dd", new Date());
  return isValid(d) ? d : undefined;
}

function dateToIso(d) {
  return d ? format(d, "yyyy-MM-dd") : "";
}

export function DatePickerFR({
  value,
  onChange,
  disabled = false,
  placeholder = "JJ/MM/AAAA",
  className,
  "data-testid": testId,
  id,
}) {
  const [open, setOpen] = React.useState(false);
  const date = isoToDate(value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          id={id}
          variant="outline"
          disabled={disabled}
          data-testid={testId}
          className={cn(
            "w-full justify-start text-left font-mono font-normal bg-white",
            !date && "text-muted-foreground",
            className,
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4 text-slate-400" />
          {date ? format(date, "dd/MM/yyyy", { locale: fr }) : placeholder}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={date}
          onSelect={(d) => {
            onChange?.(dateToIso(d));
            setOpen(false);
          }}
          locale={fr}
          weekStartsOn={1}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  );
}

export default DatePickerFR;
